import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { PersonnelPermissionsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PersonnelActions from "../../../actions/personnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { PersonnelSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./PersonnelPermissionsPage.scss";

interface PersonnelPermissionsPageProps {
    personnel: Dtos.Personnel;
    personnelPermissionGroups: Dtos.PersonnelPermissionGroup[];
    loadingPersonnel: boolean;
    loadPersonnelFailure: boolean;
    loadPersonnelErrorCode: string;
    loadingPersonnelPermissions: boolean;
    loadPersonnelPermissionsSuccess: boolean;
    loadPersonnelPermissionsFailure: boolean;
    loadPersonnelPermissionsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    loadPersonnel: PersonnelActions.IPersonnelLoadByIdActionCreator;
    loadPersonnelPermissions: PersonnelActions.IPersonnelPermissionsLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const PersonnelPermissionsPage: FC<PersonnelPermissionsPageProps> = ({
    personnel,
    personnelPermissionGroups,
    loadingPersonnel,
    loadPersonnelFailure,
    loadPersonnelErrorCode,
    loadingPersonnelPermissions,
    loadPersonnelPermissionsSuccess,
    loadPersonnelPermissionsFailure,
    loadPersonnelPermissionsErrorCode,
    permissions,
    clearPersonnel,
    loadPersonnel,
    loadPersonnelPermissions,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { personnelId } = useParams<{ personnelId: string }>();
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const showPermissions =
        !loadingPersonnelPermissions &&
        loadPersonnelPermissionsSuccess &&
        !creatingTree;

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load personnel & permissions
    useEffect(() => {
        if (personnelIdNumber && !personnel) {
            setDrawerItems(PersonnelSubMenu(personnelIdNumber, permissions));
            setBreadcrumbs(PersonnelPermissionsBreadcrumbs(personnelIdNumber));

            loadPersonnelPermissions(personnelIdNumber);
            loadPersonnel(personnelIdNumber);
        }
    }, [
        personnelIdNumber,
        personnel,
        setDrawerItems,
        PersonnelSubMenu,
        permissions,
        setBreadcrumbs,
        PersonnelPermissionsBreadcrumbs,
        loadPersonnel,
        loadPersonnelPermissions,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelFailure) {
            navigate(`/error/${loadPersonnelErrorCode}`);
        }
        if (loadPersonnelPermissionsFailure) {
            navigate(`/error/${loadPersonnelPermissionsErrorCode}`);
        }
    }, [loadPersonnelFailure, loadPersonnelPermissionsFailure, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (personnelIdNumber) {
            setDrawerItems(
                PersonnelSubMenu(
                    personnelIdNumber,
                    permissions,
                    personnel?.fellowships
                )
            );
            if (personnel?.firstName && personnel?.lastName) {
                setBreadcrumbs(
                    PersonnelPermissionsBreadcrumbs(
                        personnelIdNumber,
                        `${personnel.firstName} ${personnel.lastName}`
                    )
                );
            }
        }
    }, [
        personnelIdNumber,
        personnel,
        setBreadcrumbs,
        PersonnelPermissionsBreadcrumbs,
    ]);

    // Create tree
    useEffect(() => {
        if (personnelPermissionGroups && !initalizedTreeData) {
            setCreatingTree(true);

            // If no group permissions return
            if (personnelPermissionGroups.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                personnelPermissionGroup: Dtos.PersonnelPermission[]
            ) => {
                let children = [];

                children =
                    personnelPermissionGroup
                        ?.sort((a, b) =>
                            a?.permissionName?.localeCompare(b?.permissionName)
                        )
                        ?.map((y) => ({
                            id: `permission-${y.permissionName}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .USERUNLOCK
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {y?.permissionName}
                                    </div>
                                    <div className="px-1">
                                        {y?.permissionDescription}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...personnelPermissionGroups]
                    ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                    ?.map((x) => ({
                        id: `group-${x?.name}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.USERSHIELD}
                                />
                                <span className="pl-4 mb-0">{x?.name}</span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.personnelPermissions?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.personnelPermissions),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        personnelPermissionGroups,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    // Refresh tree
    useEffect(() => {
        if (personnelPermissionGroups) {
            setInitalizedTreeData(false);
        }
    }, [personnelPermissionGroups, setInitalizedTreeData]);

    // Cleanup effect to clear the group item on component unmount
    useEffect(() => {
        return () => {
            clearPersonnel();
        };
    }, [clearPersonnel]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Permissions</h1>
                    </div>
                </div>
                {!showPermissions ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPermissions ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no permissions to
                                        view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [showPermissions, treeData, handleToggle, toggleState]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelRoleView}
            loading={loadingPersonnel || loadingPersonnelPermissions}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<PersonnelPermissionsPageProps> => {
    let personnel: Dtos.Personnel | undefined = !(
        state.personnel.formData instanceof Array
    )
        ? state.personnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        personnel: personnel!,
        personnelPermissionGroups: state.personnel.groupedPersonnelPermissions,
        loadingPersonnel:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Pending,
        loadPersonnelFailure:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Failure,
        loadPersonnelErrorCode: state.personnel.loadState.errorCode,
        loadingPersonnelPermissions:
            state.personnel.loadPermissionsState &&
            state.personnel.loadPermissionsState.status == RequestState.Pending,
        loadPersonnelPermissionsSuccess:
            state.personnel.loadPermissionsState &&
            state.personnel.loadPermissionsState.status == RequestState.Success,
        loadPersonnelPermissionsFailure:
            state.personnel.loadPermissionsState &&
            state.personnel.loadPermissionsState.status == RequestState.Failure,
        loadPersonnelPermissionsErrorCode:
            state.personnel.loadPermissionsState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<PersonnelPermissionsPageProps> => {
    return {
        loadPersonnel: bindActionCreators(
            PersonnelActions.LoadPersonnelById,
            dispatch
        ),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
        loadPersonnelPermissions: bindActionCreators(
            PersonnelActions.LoadPersonnelPermissionsById,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PersonnelPermissionsPage);
